import React, { useEffect, useRef, useState } from 'react'
import { Button, Form, Input, Select } from 'antd'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'

import Styles from './apply-field-rule-tree.module.scss'
import ResponseCode from '../../../../common/response-code'
import { getExamDictData } from '../../../../common/request'
import { FieldTypeMap, RelationTypeMap, RuleLogicMap } from '../../../../common/common'
import { updateDictInfo } from '../../../../store/slice/dict-slice'
import ApplyFieldRuleLogic from './ApplyFieldRuleLogic'
import ApplyFieldRuleContent from './ApplyFieldRuleContent'
import { buildGroupedFieldSelectOptions, isNumericCompareLogic, isEmptyCheckLogic } from './applyFieldRuleOptions'

const ruleRelationOption = [
  { label: '或', value: RelationTypeMap.or },
  { label: '且', value: RelationTypeMap.and }
]

const isSelectContentValueEmpty = (value) => {
  if (value == null || value === '') {
    return true
  }

  if (Array.isArray(value)) {
    return value.length === 0 || value.every(item => item == null || item === '')
  }

  return false
}

function ApplyFieldRuleConditionRow (props) {
  const [form] = Form.useForm()
  const ruleContentRef = useRef()
  const dispatch = useDispatch()
  const dictInfo = useSelector(state => state.dict.dictInfo)
  const [fieldOptions, setFieldOptions] = useState([])
  const [selectedRule, setSelectedRule] = useState({})
  const showRelationSelect = props.rowIndex > 0
  const contentLogicType = Form.useWatch('contentLogicType', form)
  const requireSelectContent = contentLogicType != null && !isEmptyCheckLogic(contentLogicType)

  useEffect(() => {
    initRuleData()
  }, [props.conditionItem])

  useEffect(() => {
    setFieldOptions(buildGroupedFieldSelectOptions(props.availableFields, {
      valueKey: 'fieldName'
    }))
  }, [props.availableFields])

  const initRuleData = async () => {
    const formData = props.conditionItem?.formData
    if (!formData) {
      return
    }

    if (formData.fieldType === FieldTypeMap.select && formData.fieldJoinSelect) {
      await getDataDict(formData.fieldJoinSelect)
    }

    if (formData.fieldType === FieldTypeMap.cascader && formData.selectContentName?.length > 0) {
      ruleContentRef.current?.getRegionTreeAction({ value: formData.selectContentName[0] })
    }

    form.setFieldsValue({
      ruleLogicType: formData.ruleLogicType ?? RelationTypeMap.or,
      contentLogicType: formData.contentLogicType ?? RuleLogicMap.include,
      ruleName: formData.ruleName || formData.fieldName,
      selectContentName: formData.selectContentName,
      ruleItem: {
        fieldType: formData.fieldType,
        fieldName: formData.fieldName,
        fieldCode: formData.fieldCode,
        fieldJoinSelect: formData.fieldJoinSelect
      },
      selectContentItems: formData.selectContentItems
    })

    if (formData.fieldCode) {
      setSelectedRule({
        field: {
          fieldType: formData.fieldType,
          fieldName: formData.fieldName,
          fieldCode: formData.fieldCode,
          fieldJoinSelect: formData.fieldJoinSelect
        }
      })
    }
  }

  const getDataDict = async (dictCode) => {
    if (!dictCode) {
      return []
    }
    if (dictInfo[dictCode]) {
      return dictInfo[dictCode]
    }
    const res = await getExamDictData({ dictCode })
    if (res.code === ResponseCode.success) {
      dispatch(updateDictInfo({ dictKey: dictCode, dictValue: res.data }))
      return res.data
    }
    return []
  }

  const syncFormData = () => {
    if (!props.conditionItem.formData) {
      props.conditionItem.formData = {}
    }
    const preservedSelectContentItems = (props.conditionItem.formData.selectContentItems || []).filter(Boolean)
    const formValues = form.getFieldsValue()
    if (!Array.isArray(formValues.selectContentItems) || formValues.selectContentItems.some(item => !item)) {
      delete formValues.selectContentItems
    }
    Object.assign(props.conditionItem.formData, formValues, form.getFieldValue('ruleItem'))
    if (preservedSelectContentItems.length) {
      props.conditionItem.formData.selectContentItems = preservedSelectContentItems
    }
    if (!showRelationSelect) {
      delete props.conditionItem.formData.ruleLogicType
    }
  }

  const onFormChange = (fieldName, key, item) => {
    if (fieldName === 'ruleName') {
      const field = item?.field || props.availableFields?.find(fieldItem => {
        return fieldItem.fieldName === form.getFieldValue('ruleName')
      })
      if (!field) {
        return
      }
      setSelectedRule({ field })
      form.setFieldValue('ruleItem', field)
      props.conditionItem.formData.fieldCode = field.fieldCode
      props.conditionItem.formData.fieldName = field.fieldName
      props.conditionItem.formData.fieldType = field.fieldType
      props.conditionItem.formData.fieldJoinSelect = field.fieldJoinSelect
      props.conditionItem.formData.ruleName = field.fieldName
      props.conditionItem.formData.contentLogicType = RuleLogicMap.include
      props.conditionItem.formData.selectContentName = undefined
      props.conditionItem.formData.selectContentItems = undefined
      form.resetFields(['contentLogicType', 'selectContentName', 'selectContentItems'])
    }

    if (fieldName === 'contentLogicType') {
      const nextLogicType = form.getFieldValue('contentLogicType')
      const prevLogicType = props.conditionItem.formData.contentLogicType
      const shouldClearContent = nextLogicType !== prevLogicType && (
        isNumericCompareLogic(nextLogicType) !== isNumericCompareLogic(prevLogicType)
        || isEmptyCheckLogic(nextLogicType) !== isEmptyCheckLogic(prevLogicType)
      )
      if (shouldClearContent) {
        form.setFieldValue('selectContentName', undefined)
        form.setFieldValue('selectContentItems', undefined)
        props.conditionItem.formData.selectContentName = undefined
        props.conditionItem.formData.selectContentItems = undefined
        form.setFields([
          { name: 'selectContentName', errors: [] }
        ])
      } else if (!isEmptyCheckLogic(nextLogicType)) {
        form.validateFields(['selectContentName']).catch(() => {})
      }
    }

    if (fieldName === 'selectContentName') {
      if (FieldTypeMap.select === selectedRule.field?.fieldType) {
        const optionList = Array.isArray(item?.option)
          ? item.option
          : (Array.isArray(item) ? item : [])
        const items = optionList
          .map(data => data?.dict)
          .filter(Boolean)
        form.setFieldValue('selectContentItems', items)
        props.conditionItem.formData.selectContentItems = items
        props.conditionItem.formData.selectContentName = item?.value ?? form.getFieldValue('selectContentName')
      } else if (FieldTypeMap.cascader === selectedRule.field?.fieldType) {
        form.setFieldValue('selectContentItems', item)
        props.conditionItem.formData.selectContentItems = item
        props.conditionItem.formData.selectContentName = form.getFieldValue('selectContentName')
      } else {
        props.conditionItem.formData.selectContentName = form.getFieldValue('selectContentName')
      }
    }

    syncFormData()
    props.onChange?.()
  }

  const formFieldsChange = (changedFields) => {
    if (!props.conditionItem?.formData || !changedFields?.length) {
      return
    }
    syncFormData()
    props.onChange?.()
  }

  const relationWidth = 72

  return (
    <div className={ Styles.conditionRow }>
      {
        !showRelationSelect && (
          <div className={ Styles.conditionRowRelationPlaceholder } style={ { width: relationWidth } }></div>
        )
      }
      <div className={ Styles.conditionRowForm }>
        <Form
          form={ form }
          layout="inline"
          size="small"
          initialValues={ {
            contentLogicType: RuleLogicMap.include,
            ruleLogicType: RelationTypeMap.or
          } }
          onFieldsChange={ formFieldsChange }
        >
          {
            showRelationSelect && (
              <Form.Item name="ruleLogicType">
                <Select
                  disabled={ props.disabled }
                  options={ ruleRelationOption }
                  style={ { width: relationWidth } }
                  onChange={ onFormChange.bind(this, 'ruleLogicType', 'ruleLogicType') }
                ></Select>
              </Form.Item>
            )
          }
          <Form.Item
            name="ruleName"
            rules={ [{ required: true, message: '请选择字段' }] }
          >
            <Select
              disabled={ props.disabled }
              showSearch
              optionFilterProp="label"
              options={ fieldOptions }
              placeholder="请选择字段"
              style={ { width: 180 } }
            onChange={ (value, option) => {
              const field = option?.field || props.availableFields?.find(fieldItem => {
                return fieldItem.fieldName === value
              })
              form.setFieldValue('ruleName', value)
              onFormChange('ruleName', 'ruleName', { field })
            } }
          ></Select>
        </Form.Item>
        <Form.Item
          name="contentLogicType"
          rules={ [{ required: true, message: '请选择内容逻辑' }] }
        >
          <ApplyFieldRuleLogic
            disabled={ props.disabled }
            ruleItem={ selectedRule.field }
            value={ form.getFieldValue('contentLogicType') }
            onChange={ (val) => {
              form.setFieldValue('contentLogicType', val)
              onFormChange('contentLogicType', 'contentLogicType', val)
            } }
          ></ApplyFieldRuleLogic>
        </Form.Item>
        <Form.Item
          name="selectContentName"
          rules={ requireSelectContent ? [
            {
              validator: (_, value) => {
                if (isEmptyCheckLogic(form.getFieldValue('contentLogicType'))) {
                  return Promise.resolve()
                }
                if (isSelectContentValueEmpty(value)) {
                  return Promise.reject(new Error('请填写规则内容'))
                }
                return Promise.resolve()
              }
            }
          ] : [] }
        >
          <ApplyFieldRuleContent
            disabled={ props.disabled }
            ref={ ruleContentRef }
            ruleItem={ selectedRule.field }
            contentLogicType={ form.getFieldValue('contentLogicType') }
            value={ form.getFieldValue('selectContentName') }
            onChange={ (value, option) => {
              onFormChange('selectContentName', 'selectContentName', { value, option })
              if (requireSelectContent) {
                form.validateFields(['selectContentName']).catch(() => {})
              }
            } }
          ></ApplyFieldRuleContent>
        </Form.Item>
        <Form.Item name="ruleItem" hidden>
          <Input></Input>
        </Form.Item>
          <Form.Item name="selectContentItems" hidden>
            <Input></Input>
          </Form.Item>
        </Form>
      </div>
      {
        !props.disabled && (
          <div className={ Styles.conditionRowActions }>
            <Button
              type="text"
              size="small"
              icon={ <PlusOutlined/> }
              title="添加条件"
              onClick={ props.onAddRow }
            ></Button>
            {
              props.canRemove && (
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={ <MinusOutlined/> }
                  title="删除条件"
                  onClick={ props.onRemoveRow }
                ></Button>
              )
            }
          </div>
        )
      }
    </div>
  )
}

export default ApplyFieldRuleConditionRow
